import { createContext, type ReactNode, type RefObject, useContext } from 'react';

import type {
  AutocompleteAction,
  FieldSampleValue,
  RelationalFieldOption,
  RenderAutocompleteDetails,
} from '../../types/QueryBuilder';

import type { GridSelectionState } from './hooks/useGridSelection';

export type { FieldSampleValue };

/** Lazy value suggestions for fields with empty static sampleValues; empty list is fine. */
export type LoadFieldValues = (field: string) => Promise<readonly FieldSampleValue[]>;
/** Notifies consumers when a field autocomplete input changes. */
export type FieldSearchHandler = (query: string, scope?: string) => void;

export interface FilterBarContextValue {
  /** When true, the filter bar is read-only — token cells are disabled, popovers don't
   * open, remove buttons are hidden, and auto-inserted FreeText cursor inputs are skipped.
   * Defaults to false (editable). */
  readonly: boolean;
  /** Grid selection state — null when rendered outside a QueryBuilder with a token list. */
  gridState: GridSelectionState | null;
  /** When set, applied as the `id` of the trailing FreeTextToken input for label association. */
  inputId?: string;
  /**
   * When true (default), the field-key autocomplete options render an inline
   * type chip (e.g. "string" / "number" / "boolean") next to each field name.
   * Set to false from the FilterBar prop to suppress the chip — useful for
   * field sets where the type is not meaningful to the consumer (e.g. the
   * flag-filters domain, where every field has a fixed shape).
   */
  showTypeLabels: boolean;
  /** Set false to stop the operator picker offering "exists" / "does not exist"; unset offers them. */
  allowExistsOption?: boolean;
  /**
   * Optional list of typed-group prefixes (e.g. anySpan / noSpan). When provided
   * the FreeText autocomplete surfaces a "Relational Fields" section with one
   * Group entry per option, and the LogicParen type cell offers them in its
   * autocomplete. When undefined or empty, no Relational Fields section is shown.
   */
  relationalFieldOptions?: RelationalFieldOption[];
  omitFields?: string[];
  /** Placeholder for the trailing FreeText input while the bar is empty. Undefined = none. */
  placeholder?: string;
  autocompleteFooter?: ReactNode;
  autocompleteHeader?: ReactNode;
  autocompleteActions?: AutocompleteAction[];
  /** Renders the metadata panel beside the autocomplete list for the highlighted option. */
  autocompleteDetails?: RenderAutocompleteDetails;
  autocompleteAlignRef?: RefObject<HTMLElement | null>;
  onLoadValues?: LoadFieldValues;
  onFieldSearch?: FieldSearchHandler;
  consumeRestoredDraft?: (key: string) => string | undefined;
}

export const FilterBarContext = createContext<FilterBarContextValue>({
  readonly: false,
  gridState: null,
  showTypeLabels: true,
});

export function useFilterBarContext(): FilterBarContextValue {
  return useContext(FilterBarContext);
}
